import { useEffect, useState } from 'react';
import { useTaskWizardForm } from '../utils/mantineFormSchema';
import { transformTaskToMantineFormData } from '../utils/transformers/taskTransformers';
import { useGPSLocationSuggestion } from '@/hooks/useGPSLocationSuggestion';
import { useRoadDetectionHook } from './useRoadDetectionHook';
import { useToolbarInitialization } from './useToolbarInitialization';
import { useWizardNavigation } from './useWizardNavigation';
import { useTaskSubmission } from './useTaskSubmission';
import { useGeoMarkupStore } from '../../../../store';
import { useLocalizedDate } from '../../../../hooks/useLocalizedDate';
import { WIZARD_CONSTANTS } from '../types/constants/constants';
import type { TaskStepLogic } from '../utils/validation/stepValidation';
import type { Task } from '../../../../types';

interface UseTaskWizardLogicProps {
  editingTask?: Task | null;
  steps: TaskStepLogic[];
}

export const useTaskWizardLogic = ({
  editingTask,
  steps,
}: UseTaskWizardLogicProps) => {
  // Get localized date formatting function
  const { formatForInput } = useLocalizedDate();

  // Track initial location value to detect changes (for unsaved changes dialog)
  const [initialLocationValue, setInitialLocationValue] = useState<
    string | null
  >(null);

  // Track if we're waiting for initial GPS detection (CREATE mode with anchorPoint)
  const [isWaitingForInitialGPS, setIsWaitingForInitialGPS] = useState(false);

  // Initialize Mantine form with task data (edit mode) or defaults (create mode)
  const { form, goToStep } = useTaskWizardForm(
    editingTask
      ? transformTaskToMantineFormData(editingTask, formatForInput)
      : undefined,
    steps // Dynamic section list (Related Tasks optional)
  );

  // Initialize toolbar geometry when form opens
  useToolbarInitialization(editingTask);

  // ✅ Initialize anchor point in Zustand store
  const setAnchorPoint = useGeoMarkupStore((s) => s.tasks.setFormAnchorPoint);

  useEffect(() => {
    // EDIT MODE: Load anchor from task to Zustand
    if (editingTask) {
      const anchor = editingTask.anchorPoint || null;
      setAnchorPoint(anchor);
      setIsWaitingForInitialGPS(false); // No auto-GPS in edit mode
    } else {
      // CREATE MODE: Only set default if anchor point is not already set
      // (anchor point may already be set by createTaskAt from context menu)
      const currentAnchor = useGeoMarkupStore.getState().tasks.formAnchorPoint;
      if (!currentAnchor) {
        // Don't force a default location - let it be null
        // GPS detection will not trigger for null values (guarded by isValidForGPSDetection)
        // Map viewport can independently use DEFAULT_MAP_CENTER for initial view
        setAnchorPoint(null);
        setIsWaitingForInitialGPS(false);
      } else {
        // AnchorPoint exists + location is empty → GPS will auto-detect
        // Keep field disabled until GPS completes
        if (!form.values.location) {
          setIsWaitingForInitialGPS(true);
        }
      }
    }
    // Note: 'form' intentionally omitted from deps - setFieldValue is stable,
    // effect should only run when task ID changes (mode switch or different task)
    // Using editingTask?.id (primitive) instead of editingTask (object) prevents
    // unnecessary re-runs when selectedTask reference changes (code-refactor.md compliance)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingTask?.id, setAnchorPoint]);

  // GPS location suggestion for automatic road/address detection. No
  // geometry-backed road list is supplied, so detection resolves suggestions
  // via reverse geocoding only (client-side proximity matching is inactive).
  const gpsLocationSuggestion = useGPSLocationSuggestion({
    maxRoadDistance: WIZARD_CONSTANTS.GPS_MAX_ROAD_DISTANCE_METERS,
    autoDetect: true,
  });

  // Use extracted hooks (side-effects only - no return value needed)
  const roadDetectionHook = useRoadDetectionHook({
    form,
    editingTask,
    gpsLocationSuggestion,
    onGPSComplete: () => setIsWaitingForInitialGPS(false),
  });

  // Capture initial location value after first GPS detection (CREATE mode only)
  // This allows unsaved changes detection to compare against the GPS-detected initial value
  useEffect(() => {
    // In CREATE mode: lock in first location value as initial (typically from GPS)
    // In EDIT mode: don't track initial (uses editingTask.location for comparison)
    if (!editingTask && !initialLocationValue && form.values.location) {
      setInitialLocationValue(form.values.location);
    }
  }, [editingTask, form.values.location, initialLocationValue]);

  const wizardNavigation = useWizardNavigation({
    form,
    editingTask,
    goToStep,
    initialLocationValue,
  });

  const taskSubmission = useTaskSubmission({
    form,
    editingTask,
    forceClose: wizardNavigation.forceClose,
    goToStep, // Enable error navigation
  });

  return {
    // Form state
    form,

    // Submission state
    isSubmitting: taskSubmission.isSubmitting,

    // Task data
    editingTask,
    editSessionId: taskSubmission.editSessionId, // For batching history during edits

    // Handlers
    handleClose: wizardNavigation.handleClose,
    forceClose: wizardNavigation.forceClose,
    handleSubmit: taskSubmission.handleSubmit,
    handleStepClick: wizardNavigation.handleStepClick,
    handleManualRoadInput: roadDetectionHook.handleManualRoadInput,
    handleRequestGPSDetection: roadDetectionHook.handleRequestGPSDetection,

    // GPS Location Suggestion
    gpsLocationSuggestion: {
      suggestion: gpsLocationSuggestion.suggestion,
      isDetecting: gpsLocationSuggestion.isDetecting,
      error: gpsLocationSuggestion.error,
      detectLocation: gpsLocationSuggestion.detectLocation,
      clearSuggestion: gpsLocationSuggestion.clearSuggestion,
    },

    // Road source tracking
    roadSource: roadDetectionHook.roadSource,
    isWaitingForInitialGPS,
  };
};
